import React, { useEffect, useRef } from 'react';
import { Globe, Smartphone, ShoppingCart, TrendingUp, Search, Code, Layout, Database, ShieldCheck, ArrowRight, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';
import Typed from 'typed.js';
import SEO from '../components/SEO';

const Services = () => {
    const typedElement = useRef(null);

    useEffect(() => {
        const typed = new Typed(typedElement.current, {
            strings: [
                'Web Development',
                'Mobile Apps',
                'Cloud Solutions',
                'E-Commerce',
                'Digital Marketing',
                'Custom Software',
                'UI/UX Design',
                'Cybersecurity'
            ],
            typeSpeed: 70,
            backSpeed: 45,
            backDelay: 1800,
            loop: true,
            showCursor: true,
            cursorChar: '|',
        });

        return () => typed.destroy();
    }, []);

    const allServices = [
        { title: 'Website Development', description: 'We build fast, secure, and visually stunning websites using modern frameworks like React, Next.js, and WordPress.', icon: Globe, path: '/services/website-development' },
        { title: 'Mobile App Development', description: 'Custom iOS and Android applications designed with performance and scalability in mind. iOS, Android, and Cross-platform.', icon: Smartphone, path: '/services/android-development' },
        { title: 'E-commerce Solutions', description: 'End-to-end e-commerce development including payment gateway integration and inventory management.', icon: ShoppingCart, path: '/services/ecommerce' },
        { title: 'Digital Marketing', description: 'Comprehensive digital strategies including social media management, PPC, and content marketing.', icon: TrendingUp, path: '/services/digital-marketing' },
        { title: 'SEO Optimization', description: 'Advanced search engine optimization techniques to improve your organic rankings and attract high-quality leads.', icon: Search, path: '/services/seo' },
        { title: 'Custom Software', description: 'Tailor-made software solutions to automate your business processes and solve unique operational challenges.', icon: Code, path: '/services/custom-software' },
        { title: 'UI/UX Design', description: 'Focused on creating intuitive and engaging user interfaces that provide exceptional user experiences.', icon: Layout, path: '/services/ui-ux-design' },
        { title: 'Cloud Solutions', description: 'Safe and scalable cloud infrastructure management to ensure your data is always accessible.', icon: Database, path: '/services/cloud-solutions' },
        { title: 'Cyber Security', description: 'Protecting your digital assets with advanced security audits, firewalls, and data encryption.', icon: ShieldCheck, path: '/services/cyber-security' }
    ];

    return (
        <div className="bg-white dark:bg-dark transition-colors duration-500 min-h-screen">
            <SEO
                title="Our Services | Full-Stack Tech & Growth Solutions - MehareTech"
                description="Explore MehareTech's premium IT services: Web & App Development, Cloud Infrastructure, Cyber Security, E-commerce, and Digital Marketing. Engineered for speed, scale, and ROI."
                keywords="it services india, web development, android app development, digital marketing company, cloud solutions, cybersecurity audits, ecommerce development, ui ux design agency"
            />
            {/* Page Header */}
            <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 bg-slate-900 dark:bg-[#02000c] overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/20 blur-[120px]"></div>
                    <div className="absolute bottom-0 left-0 w-1/4 h-full bg-primary/5 blur-[100px]"></div>
                </div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-primary font-bold uppercase tracking-[0.2em] text-sm mb-4 block"
                    >
                        Our Arsenal
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-3xl md:text-5xl font-black text-white mb-4 md:mb-6"
                    >
                        Expert in <span className="text-gradient" ref={typedElement}></span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto"
                    >
                        Precision-engineered digital solutions to drive your competitive advantage.
                    </motion.p>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-12 md:py-24">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {allServices.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <ServiceCard {...service} />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-12 md:py-20 bg-slate-50 dark:bg-dark-accent/20 relative overflow-hidden transition-colors duration-500 border-y border-slate-100 dark:border-white/5">
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className="max-w-4xl mx-auto p-8 md:p-16 bg-white dark:bg-dark-accent/60 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl dark:shadow-primary/5 border border-slate-100 dark:border-white/10 backdrop-blur-xl"
                    >
                        <Zap size={40} className="text-primary mx-auto mb-6 group-hover:scale-110 transition-transform" />
                        <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-6 leading-tight">Ready to Scale Your Digital Presence?</h2>
                        <p className="text-base md:text-lg text-slate-500 dark:text-slate-400 mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed">
                            Lets discuss how our custom technology solutions can specifically solve your unique business hurdles.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
                            <button className="px-8 py-4 md:px-10 md:py-5 bg-primary text-white font-black rounded-2xl md:rounded-[2rem] shadow-xl shadow-primary/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 md:gap-4 group text-sm md:text-base">
                                Free Strategy Session <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button className="px-8 py-4 md:px-10 md:py-5 bg-slate-900 dark:bg-white/5 text-white dark:text-slate-300 font-black rounded-2xl md:rounded-[2rem] hover:bg-slate-800 dark:hover:bg-white/10 transition-all text-sm md:text-base">
                                Case Studies
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Services;
